import { CitationSourcesPreview } from "@/components/ai-elements/citation-pill";
import SectionHeaderWithIcon from "@/components/company/shared/section-header-with-icon";
import { Button } from "@/components/ui/button";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import type { CitationSource } from "@/lib/citation-sources";
import { sourceLink } from "@/lib/source-link";
import { cn } from "@/lib/utils";
import type { CompanyWebHighlight, CompanyWebSource } from "@shaarp/common";
import { SparklesIcon } from "lucide-react";

/**
 * "Key takeaways": numbered points, each followed by footnote chips for the
 * pages it rests on. A chip opens a preview of its page, the one the chat's
 * citation pills open: its title linking out, its site. A page keeps the same
 * number across points; a URL that is not an http(s) page gets no chip. While
 * the profile streams, the section and each point blur in as they are written,
 * and `sources` — the run's pages with their titles — is not known yet.
 */
export default function CompanyWebHighlights({
  highlights,
  sources = [],
  isWriting = false,
}: {
  highlights: CompanyWebHighlight[];
  sources?: CompanyWebSource[];
  isWriting?: boolean;
}) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();
  if (highlights.length === 0) return null;

  const points = withFootnotes(highlights, sources);

  return (
    <section className={cn(isWriting && "blur-in-appear")}>
      <SectionHeaderWithIcon
        icon={SparklesIcon}
        title={t("company.webPreview.highlights")}
        labelClassName="text-majorelle"
      />
      <div className="rounded-xl border border-majorelle/20 bg-gradient-to-br from-majorelle/[0.07] to-majorelle/[0.02] p-3.5">
        <ol className="flex list-decimal flex-col gap-2.5 pl-5 text-sm leading-relaxed marker:font-semibold marker:text-majorelle/70">
          {/* The server does not dedupe the points; they only grow in order. */}
          {points.map((point, index) => (
            <li
              key={`${index}|${point.text}`}
              className={cn("break-words", isWriting && "blur-in-appear")}
            >
              {point.text}
              {point.chips.map((chip) => (
                <CitationSourcesPreview
                  key={chip.source.href}
                  sources={[chip.source]}
                  isMobile={isMobile}
                  renderTrigger={(props) => (
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      aria-label={t("company.webPreview.footnote", {
                        number: chip.number,
                        host: chip.source.host,
                      })}
                      // The chip stays small; on touch its hit area grows to 44 px.
                      className="relative ml-2 h-5 min-w-5 cursor-pointer gap-0 rounded bg-majorelle/10 px-1 align-[1px] text-[10.5px] font-bold text-majorelle transition-colors hover:bg-majorelle/20 hover:text-majorelle focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[state=open]:bg-majorelle/20 max-sm:before:absolute max-sm:before:-inset-x-2 max-sm:before:-inset-y-3 max-sm:before:content-[''] sm:ml-1"
                      {...props}
                    >
                      {chip.number}
                    </Button>
                  )}
                />
              ))}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/**
 * Each point with the footnote chips of its pages, numbered in order of first
 * citation so a page cited twice keeps its number. A page is titled as the run
 * read it; one without a title, or not among the run's sources yet, is named by
 * its address.
 */
function withFootnotes(
  highlights: CompanyWebHighlight[],
  sources: CompanyWebSource[],
) {
  const titles = new Map<string, string>();
  for (const source of sources) {
    const link = sourceLink(source.url);
    const title = source.title?.trim();
    if (link && title) titles.set(link.href, title);
  }
  const numbers = new Map<string, number>();
  return highlights.map((highlight) => ({
    text: highlight.text,
    chips: highlight.urls.flatMap((url) => {
      const link = sourceLink(url);
      if (!link) return [];
      let number = numbers.get(link.href);
      if (number === undefined) {
        number = numbers.size + 1;
        numbers.set(link.href, number);
      }
      const source: CitationSource = {
        id: link.href,
        title: titles.get(link.href) ?? readableAddress(link.href),
        host: link.host,
        href: link.href,
      };
      return [{ number, source }];
    }),
  }));
}

/** A page's address as a reader would type it: no scheme, no `www.`, no trailing slash. */
function readableAddress(href: string): string {
  const url = new URL(href);
  const address = `${url.hostname.replace(/^www\./, "")}${url.pathname}`.replace(/\/$/, "");
  try {
    return decodeURI(address);
  } catch {
    // A malformed escape is shown as written.
    return address;
  }
}
